import { el } from "./dom";
import { iconLock } from "./icons";
import { showToast } from "./toast";
import type { CryptoWorkerClient } from "../lib/workerClient";
import { humanizeGeneric } from "../lib/humanizeError";
import { renderSnippetTabs } from "./codeSnippets";

const ENCRYPT_STRING_SNIPPETS = {
  sdk: `import { encryptData } from "secure-crypt-sdk";

const ciphertext = await encryptData(plaintextBytes, recipientPublicKey);
// Anonymous-sender sealed box - the recipient can open it, but can't
// tell who sealed it.`,
  cli: `python -m cli.main encrypt-string \\
  --pubkey recipient_public.key \\
  --in "hello" --out msg.enc`,
};

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}
function fromHex(hex: string): Uint8Array {
  const clean = hex.trim();
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function renderEncryptStringPanel(worker: CryptoWorkerClient): HTMLElement {
  const pubKeyInput = el("input", { type: "text", placeholder: "Recipient's public key (hex)", id: "enc-str-pubkey", class: "mono-field" });
  const plaintextInput = el("textarea", { id: "enc-str-plaintext", rows: "4", placeholder: "Write your message here" }, []);
  const output = el("textarea", { id: "enc-str-output", rows: "4", readonly: "true", class: "mono-field", placeholder: "Ciphertext appears here" }, []);
  const btn = el("button", { type: "button", class: "primary-btn" }, [iconLock(), "Encrypt string"]);

  btn.addEventListener("click", async () => {
    try {
      const publicKey = fromHex(pubKeyInput.value);
      const plaintext = new TextEncoder().encode(plaintextInput.value);
      plaintextInput.value = "";
      const { ciphertext } = await worker.encryptString(plaintext, publicKey);
      output.value = toHex(ciphertext);
      showToast("Sealed. Only the matching private key can open it.", "success");
    } catch (e) {
      showToast(humanizeGeneric(e, "That didn't encrypt - check the public key and try again."), "error");
    }
  });

  return el("section", {}, [
    el("p", { class: "eyebrow" }, ["Strings · Encrypt"]),
    el("h2", {}, ["Seal a short message"]),
    el("p", { class: "lede" }, [
      "Encrypt a message for a specific recipient's public key. Only the holder of the matching private key can open it.",
    ]),
    el("p", { class: "section-label" }, ["Integration"]),
    renderSnippetTabs(ENCRYPT_STRING_SNIPPETS),
    el("p", { class: "section-label" }, ["Try it live"]),
    el("div", { class: "card" }, [
      el("p", { class: "field-label" }, ["Recipient's public key"]),
      pubKeyInput,
      el("p", { class: "field-label" }, ["Message"]),
      plaintextInput,
      el("div", { class: "actions" }, [btn]),
      el("p", { class: "field-label" }, ["Ciphertext"]),
      output,
    ]),
  ]);
}
